import { ensureReference } from "@knyt/artisan";
import { EventStation } from "event-station";
/**
 * An event emitter that facilitates dispatching events asynchronously.
 *
 * @remarks
 *
 * This class partially implements the interface of native event emitters
 * to dispatch events on both a given event emitter, which is typically a DOM element,
 * and an internal event station asynchronously.
 *
 * The goal is to provide a way to dispatch native-like events asynchronously in a similar manner
 * to the native interfaces facilitating better interoperability.
 */
export class BasicEmitter {
    /**
     * A reference to a paired dispatcher that can be another event emitter
     * or a target element to dispatch events on.
     *
     * A dispatched event will be dispatched synchronously on this dispatcher first,
     * and then dispatched asynchronously on the internal event station.
     *
     * @defaultValue null
     */
    #pairedDispatcher$;
    #station = new EventStation();
    /**
     * @param pairedDispatcher - Another event emitter or a target element to dispatch events on.
     */
    /*
     * ### Private Remarks
     *
     * The `pairedDispatcher` parameter is always required, by design.
     * This is to ensure that the event emitter is always intentionally
     * either associated with another emitter or not.
     *
     * If `null` is passed, the event will only be dispatched on the internal event station;
     * not dispatching events in the DOM.
     */
    constructor(pairedDispatcher) {
        this.#pairedDispatcher$ = ensureReference(pairedDispatcher);
    }
    /**
     * Dispatch the event on the target element and the event station.
     *
     * If the target element is not set, the event will only be dispatched on the event station.
     *
     * @remarks
     *
     * The event should be defined on `HTMLElementEventMap`.
     *
     * ```ts
     * define global {
     *   interface HTMLElementEventMap {
     *     "custom-event": CustomEvent;
     *   }
     * }
     * ```
     *
     * @see HTMLElementEventMap
     */
    async dispatchEvent(customEvent) {
        // Dispatch the event synchronously on the target element first.
        this.#pairedDispatcher$.value?.dispatchEvent(customEvent);
        // Then, dispatch the event asynchronously on the event station.
        await this.#station.emitAsync(customEvent.type, customEvent);
        return !customEvent.cancelable || !customEvent.defaultPrevented;
    }
    addEventListener(type, listener, options) {
        let listeners;
        if (typeof options === "object" && options.once) {
            listeners = this.#station.once(type, listener);
        }
        else {
            listeners = this.#station.on(type, listener);
        }
        if (typeof options === "object" && options.signal) {
            options.signal.addEventListener("abort", () => listeners.off(), {
                once: true,
            });
        }
    }
    removeEventListener(type, listener, _options) {
        this.#station.off(type, listener);
    }
    replaceEventListener(type, listenerPair, options) {
        const [prevListener, nextListener] = listenerPair;
        if (prevListener) {
            this.removeEventListener(type, prevListener, options);
        }
        if (nextListener) {
            this.addEventListener(type, nextListener, options);
        }
    }
    /**
     * @deprecated
     */
    // TODO: Remove this after the migration to the new API is complete.
    #createEventListenerPropertyChangeHandler(domEventNameByPropertyName) {
        return ({ currentValue, previousValue, propertyName, }) => {
            const domEventName = domEventNameByPropertyName[propertyName];
            if (!domEventName)
                return;
            const listenerPair = [previousValue, currentValue];
            this.replaceEventListener(domEventName, listenerPair);
        };
    }
    /**
     * Creates a property change handler that replaces the event listener for the associated DOM event.
     *
     * @remarks
     *
     * This can be used as a standalone function.
     *
     * @deprecated
     */
    // TODO: Remove this after the migration to the new API is complete.
    createEventListenerPropertyChangeHandler = this.#createEventListenerPropertyChangeHandler.bind(this);
}
